/**
 * Feature-scoped multi-cap greedy packer + bounded-concurrency pool for the config-gated
 * multi-batch capability (`runConfig.ts`'s `MULTI_BATCH`). Local to `RunController.ts`, the only
 * importer — no concurrency/async library dependency.
 *
 * `createBatchPool` isn't a classic "await each task" pool: a chunk's own `executeBatch`/
 * `streamBatch` calls settle almost immediately, while completion is a later, asynchronous
 * `batch-completed` SSE event only `RunController.ts` observes. So freeing a slot must be driven by
 * an explicit `release(id)` call from whichever event fires first — this pool only ever reports
 * "what should start now"/"what should start next"; it never starts anything itself.
 *
 * `packRules` replaces a rule-count-only chunker because rule count alone doesn't bound script
 * count (a CONSISTENCY rule with N tags emits N scripts) or context-key count (a chunk's key count
 * is the union of its rules' keys, which are shared between rules referencing the same tag/object,
 * not summed). It closes a chunk as soon as adding the next rule would exceed any of the three caps.
 */

/** One rule's contribution to a chunk's three caps — see `packRules` below. */
export interface RuleCost {
  /** Identifies the rule in the `console.error` diagnostic logged when it alone exceeds a cap. */
  readonly id: string;
  /** Scripts this rule contributes to the unified batch (0 for a fully-early rule). */
  readonly scriptCount: number;
  /** Context keys this rule needs (`OBJ_*` / `CELL_<ruleId>_*` — see `RulesRunnerHelper.ts`'s
   * `buildUnifiedRunBatch`). Two rules can legitimately share a key (same referenced tag/object). */
  readonly contextKeys: readonly string[];
}

/** The three backend-mirroring caps a chunk must never exceed — see `runConfig.ts`'s `MULTI_BATCH`. */
export interface PackingCaps {
  readonly maxRulesPerBatch: number;
  readonly maxScriptsPerBatch: number;
  readonly maxContextKeysPerBatch: number;
}

/**
 * Greedily packs `items` (in order) into consecutive chunks, closing the current chunk and
 * starting a fresh one as soon as adding the next item would exceed any of `caps`' three limits
 * (rule count, script count, or context-key union size — using the union, never a per-rule sum, so
 * rules sharing a key don't force an unnecessary split).
 *
 * An item is never split across chunks; order is preserved.
 *
 * Edge case — an item that alone exceeds the script or context-key cap is placed in its own chunk
 * regardless (splitting a rule's scripts across chunks is never an option) and a `console.error`
 * diagnostic is logged; the backend batch may still reject such a chunk.
 *
 * A non-positive cap is treated as "no limit" for that dimension (defensive only).
 */
export function packRules<T>(items: readonly T[], costOf: (item: T) => RuleCost, caps: PackingCaps): T[][] {
  if (items.length === 0) return [];

  const maxRules = caps.maxRulesPerBatch > 0 ? caps.maxRulesPerBatch : Number.POSITIVE_INFINITY;
  const maxScripts = caps.maxScriptsPerBatch > 0 ? caps.maxScriptsPerBatch : Number.POSITIVE_INFINITY;
  const maxContextKeys = caps.maxContextKeysPerBatch > 0 ? caps.maxContextKeysPerBatch : Number.POSITIVE_INFINITY;

  const chunks: T[][] = [];
  let chunkItems: T[] = [];
  let chunkScriptCount = 0;
  let chunkContextKeys = new Set<string>();

  const closeChunk = (): void => {
    if (chunkItems.length === 0) return;
    chunks.push(chunkItems);
    chunkItems = [];
    chunkScriptCount = 0;
    chunkContextKeys = new Set<string>();
  };

  for (const item of items) {
    const cost = costOf(item);
    const newKeyCount = cost.contextKeys.reduce((n, key) => n + (chunkContextKeys.has(key) ? 0 : 1), 0);

    const wouldExceedCaps =
      chunkItems.length > 0 &&
      (chunkItems.length + 1 > maxRules ||
        chunkScriptCount + cost.scriptCount > maxScripts ||
        chunkContextKeys.size + newKeyCount > maxContextKeys);

    if (wouldExceedCaps) closeChunk();

    const startingFreshChunk = chunkItems.length === 0;
    const aloneExceedsCaps = cost.scriptCount > maxScripts || cost.contextKeys.length > maxContextKeys;

    if (startingFreshChunk && aloneExceedsCaps) {
      console.error(
        `[batchPool] Rule "${cost.id}" alone exceeds a per-batch cap and cannot be split across ` +
          `chunks (scripts: ${cost.scriptCount}/${caps.maxScriptsPerBatch}, contextKeys: ` +
          `${cost.contextKeys.length}/${caps.maxContextKeysPerBatch}). Placing it in its own chunk; ` +
          `the backend batch limit may still reject it.`
      );
      chunkItems.push(item);
      chunkScriptCount = cost.scriptCount;
      chunkContextKeys = new Set(cost.contextKeys);
      closeChunk(); // never let a later item merge into an already-oversized solo chunk.
      continue;
    }

    chunkItems.push(item);
    chunkScriptCount += cost.scriptCount;
    for (const key of cost.contextKeys) chunkContextKeys.add(key);
  }

  closeChunk();
  return chunks;
}

/** Public surface of one bounded-concurrency pool. `release` returns the next id to start rather
 * than invoking a callback itself — see the module doc comment. */
export interface BatchPool<TId> {
  /** ids that should start immediately — length is `Math.min(ids.length, concurrency)`. */
  readonly initialIds: readonly TId[];
  /** ids that start life queued, in the order they should be started, one at a time. */
  readonly queuedIds: readonly TId[];

  /**
   * Signals that the started `id` reached a terminal state, freeing its slot. Returns the next
   * still-queued id to start (the caller starts it), or `undefined` when nothing is left queued.
   * Idempotent — a repeat call for the same id is a safe no-op, since `RunController.ts` may reach
   * "this chunk is done" from more than one code path.
   */
  release(id: TId): TId | undefined;
}

/**
 * Builds a pool over `ids` with the given `concurrency` (clamped to at least 1). The caller
 * remains in control of what "starting" an id actually does; this only tracks which ids start now
 * vs later.
 */
export function createBatchPool<TId>(ids: readonly TId[], concurrency: number): BatchPool<TId> {
  const limit = Math.max(1, concurrency);
  const initialIds = ids.slice(0, limit);
  const queue: TId[] = ids.slice(limit);
  const releasedIds = new Set<TId>();

  return {
    initialIds,
    queuedIds: [...queue],
    release(id: TId): TId | undefined {
      if (releasedIds.has(id)) return undefined; // already released — idempotent no-op.
      releasedIds.add(id);
      return queue.shift();
    },
  };
}
